import { inventory, evolvePhases, movePhase, PHASES, totalMaterial, type MaterialId, type MaterialInventory } from './materials';
import { atmosphericColumn, climateGeometry, pressurePerUnit, type ClimateGeometry } from './climate-geometry';

export interface BodyComposition {
  readonly name: string;
  readonly materials: readonly MaterialInventory[];
  readonly layer: 'surface' | 'envelope' | 'stellar';
  readonly initialTemperature: number;
  readonly internalHeatFluxWm2: number;
  /** Dimensionless material heat-capacity multiplier; reference value 12. */
  readonly thermalCapacity: number;
  readonly geometry: ClimateGeometry;
  readonly fixedReservoirs?: boolean;
  readonly fusion?: {
    readonly coreTemperature: number;
    readonly reactionRate: number;
    readonly energyPerReaction: number;
  };
}

/** Partition a specified mixture using material properties, not a planet template. */
export function world(name: string, temperature: number, geometry: ClimateGeometry,
  amounts: Partial<Record<MaterialId, number>>,
  options: Partial<Pick<BodyComposition, 'layer' | 'internalHeatFluxWm2' | 'thermalCapacity'>> = {},
): BodyComposition {
  const materials = Object.entries(amounts).filter(([, amount]) => amount > 0)
    .map(([id, amount]) => inventory(id as MaterialId, { solid: amount }));
  // Relax the initial partition against derived pressure, not a named-world
  // pressure target. The starting temperature is fixed during initialization.
  for (let iteration = 0; iteration < 64; iteration += 1) {
    const column = atmosphericColumn(materials, geometry);
    for (const stock of materials) evolvePhases(stock, temperature, column.pressureBar, 8,
      column.gasUnits > 1e-12 ? column.pressureBar / column.gasUnits : pressurePerUnit(stock.material, geometry));
  }
  // Numerical relaxation tails must not create visible seas of trace nitrogen
  // on an otherwise dry rock. Transfer dust to the dominant phase, never delete it.
  for (const stock of materials) {
    const dominant = PHASES.reduce((a, b) => stock.phases[a] >= stock.phases[b] ? a : b);
    const threshold = totalMaterial(stock) * 1e-10;
    for (const phase of PHASES) if (phase !== dominant && stock.phases[phase] < threshold) {
      movePhase(stock, phase, dominant, stock.phases[phase]);
    }
  }
  if (options.layer === 'envelope') for (const stock of materials) {
    stock.suspended.solid = stock.phases.solid * 0.7;
    stock.suspended.liquid = stock.phases.liquid * 0.7;
  }
  return { name, geometry, layer: 'surface', initialTemperature: temperature,
    internalHeatFluxWm2: 0, thermalCapacity: 12, ...options,
    materials,
  };
}

export const STAR_COMPOSITION: BodyComposition = {
  name: 'The Kiln', layer: 'stellar', initialTemperature: 5772, internalHeatFluxWm2: 0, thermalCapacity: 1e15,
  geometry: climateGeometry(696_000_000, 274, 1_000, 0),
  fixedReservoirs: true,
  materials: [inventory('hydrogen', { plasma: 8e14 }), inventory('helium', { plasma: 2e14 })],
  fusion: { coreTemperature: 15_000_000, reactionRate: 1e-15, energyPerReaction: 5 },
};
